import React, { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, File as FileIcon, X, CheckCircle, Loader2 } from 'lucide-react'
import { Button } from './ui/button'
import { Card, CardContent } from './ui/card'
import { cn, formatFileSize, isValidFileType } from '../lib/utils'
import { conversationalApi, ProjectDocument } from '../services/conversationalApi'

interface FileUploadProps {
  onFileUpload: (files: File[]) => void
  projectId?: string
  disabled?: boolean
  className?: string
  maxFiles?: number
}

interface UploadedFile {
  file: File
  content: string
  status: 'pending' | 'uploading' | 'success' | 'error'
  error?: string
}

export function FileUpload({ onFileUpload, projectId, disabled = false, className, maxFiles = 10 }: FileUploadProps) {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [persistedDocuments, setPersistedDocuments] = useState<ProjectDocument[]>([])
  const [isDragActive, setIsDragActive] = useState(false)
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false)

  // Clear uploaded files and fetch documents when project changes
  React.useEffect(() => {
    console.log('📂 Project changed, clearing uploaded files and fetching documents. New projectId:', projectId)
    setUploadedFiles([])
    
    // Fetch persisted documents for the new project
    const fetchDocuments = async () => {
      if (!projectId) {
        setPersistedDocuments([])
        return
      }
      
      setIsLoadingDocuments(true)
      try {
        const response = await conversationalApi.getDocuments(projectId)
        setPersistedDocuments(response.documents)
        console.log(`📂 Loaded ${response.documents.length} documents for project ${projectId}`)
      } catch (error) {
        console.error('Failed to fetch documents:', error)
        setPersistedDocuments([])
      } finally {
        setIsLoadingDocuments(false)
      }
    }
    
    fetchDocuments()
  }, [projectId])

  const validateFile = (file: File): string | null => {
    if (!isValidFileType(file.name)) {
      return 'Invalid file type. Supported: .txt, .md, .pdf, .docx'
    }
    const maxSize = 10 * 1024 * 1024 // 10MB
    const maxSizeMB = 10
    if (file.size > maxSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1)
      return `File exceeds maximum size of ${maxSizeMB}MB (file size: ${fileSizeMB}MB)`
    }
    return null
  }

  // Expose method to update file status from parent
  React.useEffect(() => {
    (window as any).updateFileUploadStatus = (fileName: string, status: 'uploading' | 'success' | 'error', error?: string) => {
      setUploadedFiles((prev: UploadedFile[]) =>
        prev.map((f: UploadedFile) =>
          f.file.name === fileName
            ? { ...f, status, error }
            : f
        )
      )
      
      // Refresh documents list when upload succeeds
      if (status === 'success' && projectId) {
        refreshDocuments()
      }
    }
    return () => {
      delete (window as any).updateFileUploadStatus
    }
  }, [projectId])

  const refreshDocuments = async () => {
    if (!projectId) return
    
    try {
      const response = await conversationalApi.getDocuments(projectId)
      setPersistedDocuments(response.documents)
      console.log(`📂 Refreshed documents: ${response.documents.length} documents`)
    } catch (error) {
      console.error('Failed to refresh documents:', error)
    }
  }

  const onDrop = useCallback(async (acceptedFiles: File[], rejectedFiles: any[]) => {
    setIsDragActive(false)
    
    // Handle rejected files
    rejectedFiles.forEach(({ file, errors }) => {
      const error = errors.map((e: any) => e.message).join(', ')
      setUploadedFiles((prev: UploadedFile[]) => [...prev, {
        file,
        content: '',
        status: 'error',
        error
      }])
    })
    
    // Validate and add accepted files
    const validFiles: File[] = []
    
    for (const file of acceptedFiles) {
      const error = validateFile(file)
      
      if (error) {
        setUploadedFiles((prev: UploadedFile[]) => [...prev, {
          file,
          content: '',
          status: 'error',
          error
        }])
      } else {
        validFiles.push(file)
        setUploadedFiles((prev: UploadedFile[]) => [...prev, {
          file,
          content: '',
          status: 'pending'
        }])
      }
    }
    
    // Notify parent if we have valid files
    if (validFiles.length > 0) {
      console.log('🔥 FileUpload component calling onFileUpload with', validFiles.length, 'files')
      onFileUpload(validFiles)
    }
  }, [onFileUpload, maxFiles, uploadedFiles])

  const { getRootProps, getInputProps, isDragActive: dropzoneActive } = useDropzone({
    onDrop,
    accept: {
      'text/plain': ['.txt'],
      'text/markdown': ['.md'],
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    multiple: true,
    maxSize: 10 * 1024 * 1024, // 10MB per file
    disabled,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false)
  })

  const removeFile = (fileToRemove: File) => {
    setUploadedFiles((prev: UploadedFile[]) =>
      prev.filter((f: UploadedFile) => f.file !== fileToRemove)
    )
  }

  return (
    <div className={cn("space-y-4", className)}>
      {/* Upload Area */}
      <Card 
        {...getRootProps()} 
        className={cn(
          "border-2 border-dashed cursor-pointer transition-colors",
          isDragActive || dropzoneActive 
            ? "border-primary bg-primary/5" 
            : "border-muted-foreground/25 hover:border-primary/50",
          disabled && "cursor-not-allowed opacity-50"
        )}
      >
        <CardContent className="flex flex-col items-center justify-center py-8 px-4">
          <input {...getInputProps()} />
          <Upload className={cn(
            "h-8 w-8 mb-4",
            isDragActive || dropzoneActive ? "text-primary" : "text-muted-foreground"
          )} />
          
          <div className="text-center">
            <p className="text-sm font-medium mb-1">
              {isDragActive || dropzoneActive 
                ? "Drop your files here" 
                : "Upload meeting notes or transcripts"
              }
            </p>
            <p className="text-xs text-muted-foreground">
              Drag & drop up to {maxFiles} files (.txt, .md, .pdf, .docx)
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Max 10MB per file, 50MB total
            </p>
          </div>
          
          <Button 
            variant="outline" 
            size="sm" 
            className="mt-4"
            disabled={disabled}
            type="button"
          >
            Choose Files
          </Button>
        </CardContent>
      </Card>

      {/* Loading State */}
      {isLoadingDocuments && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Project Documents</h4>
          <Card className="p-3">
            <div className="flex items-center space-x-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Loading documents...</p>
            </div>
          </Card>
        </div>
      )}

      {/* Persisted Documents */}
      {!isLoadingDocuments && persistedDocuments.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Project Documents ({persistedDocuments.length})</h4>
          {persistedDocuments.map((doc) => (
            <Card key={doc.id} className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FileIcon className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {doc.original_filename}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(doc.file_size)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Uploaded Files (Current Session) */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Uploading</h4>
          {uploadedFiles.map((uploadedFile, index) => (
            <Card key={index} className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FileIcon className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {uploadedFile.file.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(uploadedFile.file.size)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {uploadedFile.status === 'pending' && (
                    <div className="h-4 w-4 rounded-full border-2 border-muted" />
                  )}
                  {uploadedFile.status === 'uploading' && (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  )}
                  {uploadedFile.status === 'success' && (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  )}
                  {uploadedFile.status === 'error' && (
                    <div className="flex items-center space-x-1" title={uploadedFile.error}>
                      <X className="h-4 w-4 text-red-500" />
                      {uploadedFile.error && (
                        <span className="text-xs text-red-500 truncate max-w-[150px]">
                          {uploadedFile.error}
                        </span>
                      )}
                    </div>
                  )}
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFile(uploadedFile.file)}
                    className="h-6 w-6"
                    title="Remove file"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
